/* ===================================================================
   LogoutButton -- shared sign-out control (Hopper, 2026-06-22).

   One button used on BOTH logged-in surfaces:
     - the client dashboard chrome (AppShell side foot), and
     - the operator console (Agency top bar).

   It calls the EXISTING Supabase signOut from the session provider
   (useSession().signOut -> supabase.auth.signOut()) -- no new auth logic.
   After the session clears we navigate to /auth so the user lands on the
   login screen. The explicit navigate also makes the flow observable in
   the DEV preview, where RequireAuth's auto-redirect is bypassed.

   Three visual variants so it sits correctly in each surface:
     - "side"  : full-width nav-style item for the dashboard sidebar foot.
     - "bar"   : compact pill for the operator console top bar.
     - "more"  : mobile "More" menu row (icon + label, list-row styling).
   =================================================================== */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '../auth/useSession';
import { setToken } from '../lib/api';
import { clearOnboardingState } from '../routes/Onboarding';

const LogoutIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
    <path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 17l-5-5 5-5M5 12h12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function LogoutButton({ variant = 'side' }: { variant?: 'side' | 'bar' | 'more' }) {
  const { signOut } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function onLogout() {
    setBusy(true);
    try {
      await signOut();
    } finally {
      // M4: drop the rr_ data token AND wipe the TanStack Query cache so a
      // second login on a shared machine can never flash the prior tenant's
      // data (query keys are not tenant-scoped). U2: also clear any saved
      // onboarding progress. Then land on /auth.
      setToken(null);
      queryClient.clear();
      clearOnboardingState();
      navigate('/auth', { replace: true });
    }
  }

  if (variant === 'bar') {
    return (
      <button
        type="button"
        className="mini mini--ghost"
        onClick={onLogout}
        disabled={busy}
        aria-label="Log out"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <span style={{ width: 15, height: 15, display: 'inline-flex' }}>{LogoutIcon}</span>
        {busy ? 'Logging out...' : 'Log out'}
      </button>
    );
  }

  if (variant === 'more') {
    return (
      <button
        type="button"
        className="moremenu__row moremenu__row--logout"
        onClick={onLogout}
        disabled={busy}
        aria-label="Log out"
      >
        <span className="moremenu__ico">{LogoutIcon}</span>
        <span className="moremenu__label">{busy ? 'Logging out...' : 'Log out'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="navitem"
      onClick={onLogout}
      disabled={busy}
      aria-label="Log out"
      style={{ width: '100%', background: 'none', border: 0, cursor: 'pointer', textAlign: 'left' }}
    >
      {LogoutIcon}
      {busy ? 'Logging out...' : 'Log out'}
    </button>
  );
}

export default LogoutButton;
